import { getAnalytics, logEvent as firebaseLogEvent, isSupported } from "firebase/analytics";
import app from './firebase';

/**
 * Initialize Google Analytics
 *
 * Error tracking: Enhanced console logging (errors visible in browser DevTools)
 * Usage tracking: Google Analytics via Firebase
 *
 * E2E Testing: Analytics can be disabled by setting window.E2E_DISABLE_ANALYTICS = true
 * This reduces test noise and speeds up E2E tests. To test analytics events:
 * - Set window.E2E_ENABLE_ANALYTICS = true in specific tests
 * - Use browser console to verify analytics calls
 */

const isProduction = import.meta.env.MODE === 'production';

// Check if running in E2E test environment
// E2E tests can set window.E2E_DISABLE_ANALYTICS = true to disable analytics
// Or set window.E2E_ENABLE_ANALYTICS = true to explicitly enable for testing analytics flows
const isE2ETest = typeof window !== 'undefined' && (window as any).E2E_DISABLE_ANALYTICS === true;
const forceEnableForE2E = typeof window !== 'undefined' && (window as any).E2E_ENABLE_ANALYTICS === true;

// Initialize Google Analytics (usage tracking)
let analytics: any = null;

async function initAnalytics() {
  if (isE2ETest && !forceEnableForE2E) {
    console.log('📊 Analytics disabled for E2E tests');
    return;
  }

  if (isProduction && await isSupported()) {
    try {
      analytics = getAnalytics(app);
      if (forceEnableForE2E) {
        console.log('📊 Analytics enabled for E2E analytics testing');
      }
    } catch (error) {
      console.warn('⚠️  Google Analytics initialization failed:', error);
    }
  }
}

// Initialize analytics (async, doesn't block app startup)
initAnalytics();

/**
 * Log custom events to Google Analytics
 * Use this to track important user actions
 */
export function logEvent(eventName: string, eventParams?: Record<string, any>) {
  if (analytics && isProduction) {
    firebaseLogEvent(analytics, eventName, eventParams);
  }
}

/**
 * Log event to Google Analytics
 * Used to track important user actions
 */
export function trackEvent(eventName: string, eventParams?: Record<string, any>) {
  logEvent(eventName, eventParams);
}

